import { useState, useEffect, useContext } from "react";
import { Box, Typography, Grid, Paper, Stack, Pagination, CircularProgress, Alert } from "@mui/material";
import { MdPendingActions } from "react-icons/md";
import { FaCheckCircle, FaExclamationTriangle, FaPills } from "react-icons/fa";
import useAxios from "../../hooks/useAxios";
import AuthContext from "../../contexts/auth-context";
import { urls, toastify_status, PAGE_SIZE } from "../../components/config/variables";
import Toastify from "../../components/ui/Toastify";
import StatisticCards from "./StatisticCards";
import PendingEncounterCard from "./PendingEncounterCard";
import PendingEncounterDetails from "./PendingEncounterDetails";
import FilterModal from "../../components/filter/FilterModal";
import TuneIcon from '@mui/icons-material/Tune';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { motion } from 'framer-motion';
import { pageVariants, pageTransition } from "../../constants/GlobalStyles";
import InfoIcon from "@mui/icons-material/Info";
import { validatePendingToDispenseStatistics, validatePendingToDispenseEncounters, validatePendingToDispenseEncounter } from "../../Utils/validateHttpResponse";

const LabQueuedCptsPage = () => {
  const { operation } = useAxios();
  const { token } = useContext(AuthContext);

  const [labStats, setLabStats] = useState({});
  const [pendingEncounters, setPendingEncounters] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loadingPending, setLoadingPending] = useState(true);
  const [selectedPendingEncounter, setSelectedPendingEncounter] = useState(undefined);
  const [loadingSelectedEncounter, setLoadingSelectedEncounter] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setTodate] = useState('');
  const [providerFilter, setProviderFilter] = useState('');
  const [patientFilter, setPatientFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // used to pass to the filters modal to update the filters after delete a spesific filter
  const [newFiltersAfterDelete, setNewFiltersAfterDelete] = useState(undefined)


  const handleFetchDashboardStats = () => {
    operation(
      {
        method: "GET",
        url: urls.LAB_DASHBOARD_STATS,
        headers: { Authorization: `token ${token}` },
      },
      (res) => {
        if(validatePendingToDispenseStatistics(res)) {
          setLabStats(res);

        } else {
          Toastify({message: "Invalid response for pending-to-upload statistics.", state: toastify_status.ERROR});
        }
      },
      () => { 
        Toastify({message: "Something went wrong while fetching pending-to-upload statistics.", state: toastify_status.ERROR});

      }
    );
  }

  // Fetch pharmacy dashboard statistics
  useEffect(() => {
    handleFetchDashboardStats()
    // eslint-disable-next-line
  }, []);

  const handleFetchPendingEncounters = () => {
    setLoadingPending(true);
    const params = new URLSearchParams();
    params.set('page', page)
    params.set('page_size', PAGE_SIZE)
        if (fromDate) {
            params.set('from_date', fromDate);
        }
        if (toDate) {
            params.set('to_date', toDate);
        }
        if (providerFilter) {
            params.set('provider', providerFilter);
        }
        if (patientFilter) {
            params.set('patient', patientFilter);
        }
        if (statusFilter) {
            params.set('state', statusFilter);
        }

    operation(
      {
        method: "GET",
        url: urls.LAB_QUEUED_ENCOUNTERS + '?' + params,
        headers: { Authorization: `token ${token}` },
      },
      (res) => {
        if(validatePendingToDispenseEncounters(res)) {
          setPendingEncounters(res.results);
          setPendingCount(res.count);
        } else {
        Toastify({message: "Invalid response for pending-to-upload encounters.", state: toastify_status.ERROR});
        }
        setLoadingPending(false);
      },
      () => {
        setLoadingPending(false);
      }
    );
  }

  const handleFetchSelectedPendingEncounter = (encounterId) => {
    setLoadingSelectedEncounter(true);
    operation(
      {
        method: "GET",
        url: urls.LAB_QUEUED_ENCOUNTERS + encounterId,
        headers: { Authorization: `token ${token}` },
      },
      (res) => {
        if (validatePendingToDispenseEncounter(res)) {
          setSelectedPendingEncounter(res);

        } else {
          Toastify({ message: "Invalid response for pending-to-upload encounter.", state: toastify_status.ERROR });
        }
        setLoadingSelectedEncounter(false);
      },
      (err) => {
        setLoadingSelectedEncounter(false);
        if (err?.response?.status === 404) {
          setSelectedPendingEncounter(undefined)
          handleFetchPendingEncounters();
          Toastify({ message: 'The encounter has been completed successfully.', state: toastify_status.SUCCESS })

        }
      }
    );
  }

  // Fetch pharmacy pending encounters
  useEffect(() => {
    handleFetchPendingEncounters();
    // eslint-disable-next-line
  }, [page, fromDate, toDate, providerFilter, patientFilter, statusFilter]);


  const handlePageChange = (event, value) => {
    setPage(value);
  };


  const handlePendingEncounterClick = (encounterObj) => {
    handleFetchSelectedPendingEncounter(encounterObj.id)
  }

  const handleDispenseCpts = (encounterDispenseData) => {

    const {encounterID, selectedCpts, notes, file} = encounterDispenseData

    const formData = new FormData();
    formData.append('notes', notes);
    formData.append('cpts ', JSON.stringify(selectedCpts));
    selectedCpts.forEach((cpt) => {
      formData.append(`result_file_${cpt.encounter_cpt_id}`, file);
    });

    operation(
      {
        method: "POST",
        url: urls.LAB_TESTS_UPLOAD + encounterID + '/',
        headers: { Authorization: `token ${token}`, 'Content-Type': 'multipart/form-data' },
        data: formData,
      },
      (res) => {
        Toastify({ message: 'The cpts have been uploaded successfully.', state: toastify_status.SUCCESS })
        handleFetchDashboardStats();
        handleFetchSelectedPendingEncounter(res[0].encounter); // the encounter id is the same in each object

      },
      (err) => {
        if (err?.response?.data?.errors) {
          err.response.data.errors.map(errorMsg => Toastify({ message: errorMsg.detail, state: toastify_status.ERROR }));
        } else {
          Toastify({ message: err?.response?.data?.message || 'Cpt dispensing failed.', state: toastify_status.ERROR })
        }
      }
    );
  }

  const handleDone = (encounterToComplete) => {

    const { encounter_id, notes } = encounterToComplete
      operation(
        {
          method: "PATCH",
          url: urls.LAB_QUEUED_ENCOUNTERS + encounter_id,
          headers: { Authorization: `token ${token}` },
          data: {
            "is_lab_complete": true,
            "notes": notes
          }
      },
      (res) => {
        Toastify({ message: 'Completed successfully', state: toastify_status.SUCCESS })
        handleFetchDashboardStats();
        setSelectedPendingEncounter(undefined);
        if (page === 1) {
          handleFetchPendingEncounters();
        } else {
          setPage(1);
        }

      },
      (err) => {
        if (err?.response?.data?.errors) {
          err.response.data.errors.map(errorMsg => Toastify({ message: errorMsg.detail, state: toastify_status.ERROR }));
        } else {
          Toastify({ message: err?.response?.data?.message || 'Failed to complete the action.', state: toastify_status.ERROR })
        }
      }
    );
  }

    const handleApplyFilters = (selected) => {
        setFromDate(selected.fromDate);
        setTodate(selected.toDate);
        setProviderFilter(selected.doctor);
        setPatientFilter(selected.patient);
        setStatusFilter(selected.status);
        setPage(1);
    };

    const deleteSpesificFilter = (filterKey) => {
    const updatedFilters = {
        fromDate: fromDate,
        toDate: toDate,
        doctor: providerFilter,
        patient: patientFilter,
        status: statusFilter,
        [filterKey]: ''
    };

    handleApplyFilters(updatedFilters);
    setNewFiltersAfterDelete(updatedFilters);
};

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={pageTransition}
    >
      <Grid container spacing={3} mb={2} >
        <Grid item xs={12} sm={6} md={3}>
          <StatisticCards
            value={labStats.queued_encounters ?? 0}
            title="Queued Encounters"
            icon={<MdPendingActions size={24} color="#10b981" opacity={0.8} />}
            iconBg="#d1fae5"
            isMainCard={true}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
            <StatisticCards
              value={labStats.completed_encounters ?? 0}
              title="Completed Encounters"
              icon={<FaCheckCircle size={24} color="#22c55e" opacity={0.8} />}
              iconBg="#dcfce7"
            />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
            <StatisticCards
              value={`$${labStats.total_cost_completed_cpts}` ?? 0}
              title="Total Cost of Completed Procedures"
              icon={<FaExclamationTriangle size={24} color="#f97316" opacity={0.8} />}
              iconBg="#ffedd5"
            />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
            <StatisticCards
              value={labStats.total_cpts_uploaded ?? 0}
              title="Total Procedures Uploaded"
              icon={<FaPills size={24} color="#a855f7" opacity={0.8} />}
              iconBg="#f3e8ff"
            />
        </Grid>
      </Grid>

      <Box sx={{mb: 2, display: 'flex'}}>
        {/* Filters Button */}
      <Box
        onClick={() => setFilterOpen(true)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1,
          mr: 2,
          borderRadius: "5px",
          bgcolor: "primary.main",
          color: "white",
          cursor: "pointer",
          transition: ".2s",
          "&:hover": { opacity: 0.9 },
        }}
      >
        <TuneIcon fontSize="small" />
        <Typography fontWeight={600}>All Filters</Typography>
      </Box>
      {/* Active Filters */}
      {[
        { label: "From Date", value: fromDate,  key: "fromDate" },
        { label: "To Date", value: toDate, key: "toDate" },
        { label: "Provider", value: providerFilter, key: "doctor" },
        { label: "Patient", value: patientFilter, key: "patient" },
        { label: "Status", value: statusFilter, key: "status" },
      ]
        .filter(f => f.value)
        .map(f => (
          <Box
            key={f.key}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 1,
              mr: 1,
              borderRadius: "10px",
              bgcolor: "background.default",
              border: "1px solid",
              borderColor: "divider",
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            }}
          >
            <Typography fontWeight={500}>
              {f.label}: {f.value}
            </Typography>

            <HighlightOffIcon
              onClick={() => deleteSpesificFilter(f.key)}
              sx={{
                cursor: "pointer",
                fontSize: 18,
                color: "text.secondary",
                "&:hover": { color: "error.main" },
              }}
            />
          </Box>
        ))}
      </Box>

      <Grid container spacing={3}>
        {/* LEFT SIDE: Pending Encounters */}
        <Grid
          item
          xs={12}
          md={3}
          sx={{ height: "700px", display: "flex", flexDirection: "column" }}
        >
          {/* Content */}
          <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
            {loadingPending ? (
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress size={40} />
              </Box>
            ) : (!loadingPending && pendingEncounters.length ===0)
            ? <Alert icon={<InfoIcon fontSize="inherit" />} severity="info">
                  No data available.
              </Alert>
            : (
              <Stack spacing={2}>
                {pendingEncounters?.map((encounter) => (
                  <PendingEncounterCard
                    key={encounter.id}
                    encounter={encounter}
                    selectedPendingEncounter={selectedPendingEncounter}
                    onclick={handlePendingEncounterClick}
                  />
                ))}
              </Stack>
            )}
          </Box>

          {/* Pagination */}
          {!loadingPending && pendingCount > PAGE_SIZE && (
            <Box mt={2} display="flex" justifyContent="center">
              <Pagination
                count={Math.ceil(pendingCount / PAGE_SIZE)}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="small"
              />
            </Box>
          )}
        </Grid>



        {/* RIGHT SIDE: EMPTY PLACEHOLDER */}
        <Grid item xs={12} md={9}>
        {loadingSelectedEncounter ? (
          <Paper
            elevation={0}
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={80} />
          </Paper>
        ) : !selectedPendingEncounter ? (
          <Paper
            elevation={0}
            sx={{
              height: "100%",
              borderRadius: 3,
              border: "1px dashed #e5e7eb",
              bgcolor: "#f9fafb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography color="text.secondary">
              Select an encounter to view details
            </Typography>
          </Paper>
        ) : (
          <PendingEncounterDetails
            encounter={selectedPendingEncounter}
            handleDispenseCpts={handleDispenseCpts}
            handleDone={handleDone}
          />
        )}
      </Grid>



      </Grid>
      {/* Filter Modal */}
      <FilterModal
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApply={handleApplyFilters}
        newFiltersAfterDelete={newFiltersAfterDelete}
        initialFilters={{
          fromDate,
          toDate,
          doctor: providerFilter,
          patient: patientFilter,
          status: statusFilter,
        }}
        fields={["date", 'doctor', 'patient', 'status']}
      />
    </motion.div>
  );
};

export default LabQueuedCptsPage;
